import React, { useState, useEffect } from 'react';
import { Link, navigate } from 'gatsby';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Box
} from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import HeaderLogo from '../images/logo.png';
import { topics } from '../const/const';
import gsap from 'gsap';
import { useLocation } from '@reach/router';  // Import useLocation
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const Header = ({ pageTitle, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLargeTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
  const isXLDesktop = !isMobile && !isTablet && !isLargeTablet && !isDesktop;

  useEffect(() => {
    console.log(location.pathname);
  
  }, [])

  // Get current path using useLocation
  const location = useLocation();
  const currentPath = location.pathname;

  // State for the drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const drawerWidth = isMobile ? '70vw' : 240; // Use 50% of viewport width on mobile

  // State for desktop 'Topics' menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleTopicsMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTopicsMenuClose = () => {
    setAnchorEl(null);
  };

  // Transition function for navigating to topics
  const handleTopicClick = (color, link) => {
    handleTopicsMenuClose(); // Close the menu if open
    handleDrawerClose(); // Close the drawer if open

    const coverDiv = document.createElement('div');
    coverDiv.style.position = 'fixed';
    coverDiv.style.top = '100%'; // Start from the bottom
    coverDiv.style.left = 0;
    coverDiv.style.width = '100%';
    coverDiv.style.height = '100%';
    coverDiv.style.backgroundColor = color; // Set color from the topic
    coverDiv.style.zIndex = 1000; // Bring it to the front
    coverDiv.classList.add('gsap-cover-div'); // Add a class for easy targeting

    document.body.appendChild(coverDiv);

    // Animate the cover div sliding up
    gsap.to(coverDiv, {
      top: 0, // Slide from the bottom to top
      duration: 0.5, // Adjust as needed
      ease: 'power1.inOut',
      onComplete: () => {
        // After the slide animation, navigate to the topic page
        navigate(link, {
          state: { color }, // Pass the color to the next page
        });
      }
    });
  };

  const drawer = (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={handleDrawerClose}
      sx={{ '& .MuiDrawer-paper': { width: drawerWidth, height: '100%' } }}
    >
      <List className='header-style'>
        <ListItem disablePadding>
          <ListItemButton onClick={handleDrawerClose} component={Link} to="/">
            <img src={HeaderLogo} className='logo-style' alt="Logo" style={{ cursor: 'pointer', paddingTop: '0.5rem', paddingBottom: '0.5rem' }} />
          </ListItemButton>
        </ListItem>

        {/* Topics Menu in Drawer */}
        <ListItem disablePadding>
          <ListItemButton>
            <ListItemText primary="Topics" />
          </ListItemButton>
        </ListItem>
        {topics.map((topic) => (
          <ListItem key={topic.id} disablePadding sx={{ pl: 4 }}>
            <ListItemButton
              onClick={() => handleTopicClick(topic.color, topic.link)}
              disabled={currentPath === `${topic.link}/`}  // Disable if this is the current path
              sx={{
                color: currentPath === `${topic.link}/` ? 'gray' : '#555555',  // Make current path items visually different
              }}
            >
              <ListItemText primary={topic.topic} />
            </ListItemButton>
          </ListItem>
        ))}

        <ListItem disablePadding>
          <ListItemButton onClick={handleDrawerClose} component={Link} to="/AboutUs">
            <ListItemText primary="About Us" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleDrawerClose} component={Link} to="/ContactUs">
            <ListItemText primary="Contact Us" />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );

  return (
    <div>
      <AppBar position="fixed" sx={
        isMobile ? {
          '&.MuiAppBar-root': {
            boxShadow: 'none',
            backgroundColor: '#ffffff',
            color: '#000000',
          }
        } : isXLDesktop ? {
          '&.MuiAppBar-root': {
            pl: 18,
            pr: 18,
            boxShadow: 'none',
            backgroundColor: '#ffffff',
            color: '#000000',
          }
        } : isTablet ? {
          '&.MuiAppBar-root': {
            pl: 8,
            pr: 8,
            boxShadow: 'none',
            backgroundColor: '#ffffff',
            color: '#000000',
          }
        } : {
          '&.MuiAppBar-root': {
            pl: 10,
            pr: 10,
            boxShadow: 'none',
            backgroundColor: '#ffffff',
            color: '#000000',
          }
        }
      }>
        <Toolbar>
          {/* Logo */}
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box component={Link} to="/">
          <img src={HeaderLogo} className='logo-style' alt="Logo" style={{ cursor: 'pointer', paddingTop: '0.7rem', paddingBottom: '0.7rem' }} />
          </Box>
           
          </Typography>

          {/* Desktop Menu Items */}
          {!isMobile ? (
            <div>
              <Button
                className='header-style'
                color="inherit"
                onClick={handleTopicsMenuClick}
                sx={{
                  paddingLeft: '2rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Topics
                <ArrowDropDownIcon  />
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleTopicsMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                {topics.map((topic) => (
                  <MenuItem
                    key={topic.id}
                    onClick={() => handleTopicClick(topic.color, topic.link)}
                    disabled={currentPath === `${topic.link}/`}  // Disable if this is the current path
                    sx={{
                      color: currentPath === topic.link ? 'gray' : '#555555',  // Make current path items visually different
                      '&.MuiMenuItem-root': {
                          fontSize: '14px',
                        }
                    }}
                  >
                    {topic.topic}
                  </MenuItem>
                ))}
              </Menu>
              <Button className='header-style' color="inherit" component={Link} to="/AboutUs" sx={{
                paddingLeft: '2rem',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}>
                About Us
              </Button>
              <Button className='header-style' color="inherit" component={Link} to="/ContactUs" sx={{
                textTransform: 'none',
                paddingLeft: '2rem',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}>
                Contact Us
              </Button>
            </div>
          ) : (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              sx={{ pr: 0 }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
        {drawer}
      </AppBar>
    </div>
  );
}

export default Header;
